import React from "react";
import { TCasesDocumentType, toDisplayString as CaseDocumentTypeToCopyText } from "../../../../../domain/models/CaseDocumentType1";
import { Background } from "../../BuildingBlocks/Background";
import { BackgroundColour } from "../../BuildingBlocks/BackgroundColour";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { Border } from "../../BuildingBlocks/Border";
import { CRMColors } from "../../../models/CRMColors";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { CRMFontSizes } from "../../../models/CRMFontSizes";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { CRMIcon } from "../../CRMIcon/CRMIcon";
import { WeightBold } from "../../WeightBold/WeightBold";
import { CRMButtonQuaternary } from "../../CRMButtonQuaternary/CRMButtonQuaternary";
import { CRMButtonIcon } from "../../CRMButtonIcon/CRMButtonIcon";

export type TFileAttachmentAssignmentRowSelection =
    | { tag: "unassigned" }
    | { tag: "existing_tile"; cases_document_id: string; type: TCasesDocumentType; additional_name: string }
    | { tag: "new_tile"; type: TCasesDocumentType };

type TCRMFileAttachmentAssignmentRowProps = {
    download_token: string;
    name: string;
    selected: boolean;
    assignment: TFileAttachmentAssignmentRowSelection;
    onToggleSelected: () => void;
    onOpenTypePicker: () => void;
};

const renderAssignmentLabel = (a: TFileAttachmentAssignmentRowSelection): string => {
    if (a.tag === "unassigned") return "Pick document type";
    if (a.tag === "new_tile") return CaseDocumentTypeToCopyText(a.type);
    return a.type === "other" && a.additional_name
        ? `${CaseDocumentTypeToCopyText(a.type)} — ${a.additional_name}`
        : CaseDocumentTypeToCopyText(a.type);
};

export const CRMFileAttachmentAssignmentRow = (props: React.PropsWithChildren<TCRMFileAttachmentAssignmentRowProps>): JSX.Element => {
    const isAssigned = props.assignment.tag !== "unassigned";
    const rowBg = props.selected
        ? CRMColors.PRIMARY_16
        : CRMColors.NEUTRAL_PAPER;

    return (
        <BackgroundColour colour={rowBg} borderRadius="4px">
            <Border
                type="all"
                definition={`1px solid ${props.selected ? CRMColors.PRIMARY_8 : CRMColors.NEUTRAL_10}`}
            >
                <Background padding={CRMSpacing.MEDIUM}>
                    <SpacingRow spacing={CRMSpacing.MEDIUM} alignItems="center" childSize="auto 1fr auto">
                        {/* SELECT CHECKBOX */}
                        <div
                            className="crm-file-attachment-assignment-row__checkbox"
                            onClick={props.onToggleSelected}
                        >
                            <CRMIcon
                                iconName={props.selected ? "checkbox-checked" : "checkbox-unchecked"}
                                colour={props.selected ? "highlights-calming-pink-0" : "neutral-6"}
                                size="small"
                            />
                        </div>

                        {/* FILE NAME */}
                        <CRMParagraph fontSize={CRMFontSizes.SMALL}>
                            <WeightBold>{props.name}</WeightBold>
                        </CRMParagraph>

                        {/* ASSIGN BUTTON */}
                        {isAssigned ? (
                            <CRMButtonQuaternary
                                icon="document"
                                label={renderAssignmentLabel(props.assignment)}
                                onClick={props.onOpenTypePicker}
                            />
                        ) : (
                            <CRMButtonIcon
                                variant="quaternary"
                                icon="attach"
                                title="Pick document type"
                                onClick={props.onOpenTypePicker}
                            />
                        )}
                    </SpacingRow>
                </Background>
            </Border>
        </BackgroundColour>
    );
};
